import { useState } from "react";
import { KeyRound, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { ErrorMessage } from "./Loading";

export default function MemberGate({ onSuccess, compact = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [individualAccount, setIndividualAccount] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [emailCodeLogin, setEmailCodeLogin] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function clearFeedback() {
    setError("");
    setNotice("");
  }

  async function requestEmailCode() {
    const result = await api("/api/login/email-code/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    setCodeRequested(true);
    setEmailCode("");
    setNotice(result.message);
  }

  async function submit(event) {
    event.preventDefault();
    clearFeedback();
    setSubmitting(true);
    try {
      if (recovering) {
        const result = await api("/api/password/forgot", {
          method: "POST",
          body: JSON.stringify({ email }),
        });
        setNotice(result.message);
        return;
      }
      if (emailCodeLogin) {
        if (!codeRequested) {
          await requestEmailCode();
          return;
        }
        await api("/api/login/email-code/verify", {
          method: "POST",
          body: JSON.stringify({ email, code: emailCode }),
        });
        setEmailCode("");
        onSuccess?.();
        return;
      }
      await api("/api/login", { method: "POST", body: JSON.stringify({ email: individualAccount ? email : "", password }) });
      setPassword("");
      onSuccess?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function resendEmailCode() {
    clearFeedback();
    setSubmitting(true);
    try {
      await requestEmailCode();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  function usePasswordLogin() {
    setIndividualAccount(true);
    setRecovering(false);
    setEmailCodeLogin(false);
    setCodeRequested(false);
    setEmailCode("");
    clearFeedback();
  }

  function useSharedLogin() {
    setIndividualAccount(false);
    setRecovering(false);
    setEmailCodeLogin(false);
    setCodeRequested(false);
    setPassword("");
    setEmailCode("");
    clearFeedback();
  }

  const Heading = compact ? "h2" : "h1";
  const description = recovering
    ? "Enter your login email. For privacy, the response is the same whether or not the address is registered."
    : emailCodeLogin && codeRequested
    ? `Enter the six-digit code sent to ${email}.`
    : emailCodeLogin
    ? "Enter your individual account email and we will send a short-lived, one-time sign-in code."
    : individualAccount
    ? "Use your individual class account to update your record and see member-only photographs."
    : "Use the shared class passphrase for read-only access to member details, photographs, and reunion albums.";
  const buttonLabel = submitting
    ? recovering
      ? "Requesting…"
      : emailCodeLogin && !codeRequested
      ? "Sending code…"
      : "Signing in…"
    : recovering
    ? "Request reset link"
    : emailCodeLogin && !codeRequested
    ? "Email me a code"
    : emailCodeLogin
    ? "Sign in with code"
    : "Sign in";
  const singleField = recovering || !individualAccount || (emailCodeLogin && !codeRequested);

  return (
    <section className={compact ? "member-gate is-compact" : "member-gate"}>
      <div className="member-gate__icon"><LockKeyhole aria-hidden="true" /></div>
      <div>
        <p className="kicker">For classmates and families</p>
        <Heading>{compact ? "Sign in to open this album" : "Come into the member room"}</Heading>
        <p>{description}</p>
      </div>
      <form onSubmit={submit} className="member-gate__form">
        <div className={singleField ? "member-gate__fields is-single" : "member-gate__fields"}>
          {individualAccount && <label htmlFor={compact ? "album-email" : "member-email"}>
            Login email
            <input
              id={compact ? "album-email" : "member-email"}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              readOnly={emailCodeLogin && codeRequested}
              required
            />
          </label>}
          {!recovering && !emailCodeLogin && <label htmlFor={compact ? "album-password" : "member-password"}>
            {individualAccount ? "Password" : "Class passphrase"}
            <input
              id={compact ? "album-password" : "member-password"}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>}
          {emailCodeLogin && codeRequested && <label htmlFor={compact ? "album-email-code" : "member-email-code"}>
            Six-digit sign-in code
            <input
              id={compact ? "album-email-code" : "member-email-code"}
              name="one-time-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={emailCode}
              onChange={(event) => setEmailCode(event.target.value.replace(/\D/gu, "").slice(0, 6))}
              autoFocus
              required
            />
          </label>}
        </div>
        <div className="member-gate__actions">
          <button className="button button--ink" disabled={submitting} type="submit">
            {buttonLabel}
            {recovering && <KeyRound aria-hidden="true" />}
            {emailCodeLogin && !codeRequested && <Mail aria-hidden="true" />}
          </button>
        </div>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {notice && <p className="success-message" role="status" aria-live="polite">{notice}</p>}
        <p className="privacy-note"><ShieldCheck aria-hidden="true" /> {
          recovering
            ? "Reset links expire after seven days and can be used only once."
            : emailCodeLogin
            ? "Sign-in codes expire after ten minutes, can be tried five times, and can be used only once."
            : "Credentials stay on the server, and your session stays in a secure, HttpOnly cookie."
        }</p>
        <div className="member-gate__switches">
          {individualAccount && !recovering && !emailCodeLogin && <>
            <button className="text-link member-gate__setup" type="button" onClick={() => { setRecovering(true); setPassword(""); clearFeedback(); }}>Forgot password?</button>
            <button className="text-link member-gate__setup" type="button" onClick={() => { setEmailCodeLogin(true); setCodeRequested(false); setPassword(""); clearFeedback(); }}>Email me a one-time code</button>
          </>}
          {recovering && <button className="text-link member-gate__setup" type="button" onClick={usePasswordLogin}>Back to individual sign in</button>}
          {emailCodeLogin && <>
            {codeRequested && <button className="text-link member-gate__setup" type="button" disabled={submitting} onClick={resendEmailCode}>Send another code</button>}
            {codeRequested && <button className="text-link member-gate__setup" type="button" onClick={() => { setCodeRequested(false); setEmailCode(""); clearFeedback(); }}>Use a different email</button>}
            <button className="text-link member-gate__setup" type="button" onClick={usePasswordLogin}>Use password instead</button>
          </>}
          {!recovering && individualAccount && <button className="text-link member-gate__setup" type="button" onClick={useSharedLogin}>Use the shared class passphrase</button>}
          {!recovering && !individualAccount && <button className="text-link member-gate__setup" type="button" onClick={usePasswordLogin}>Use an individual account</button>}
        </div>
      </form>
    </section>
  );
}
